import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Card,
  Container,
  Grid,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import Radio from '@mui/material/Radio';
import SearchField from "react-search-field";
import TablePagination from '@mui/material/TablePagination';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { DataGrid } from '@mui/x-data-grid';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';

const STATUS_COLORS = {
  Running: 'green',
  Creating: 'blue',
  Stopped: 'red',
  Terminated: 'black',
};

const GenericQuizScore = ({ config, alignment, searchTerm: externalSearchTerm, setSearchTerm: setExternalSearchTerm, assignmentSearchTerm: externalAssignmentSearchTerm, setAssignmentSearchTerm: setExternalAssignmentSearchTerm, onStepChange }) => {
  const {
    labType,
    apiEndpoint,
    traineeCountEndpoint,
    fieldMapping = {
      labIdField: "lab_id",
      labNameField: "lab_Name",
      startDateField: "start_date",
      endDateField: "end_date",
    },
  } = config;

  const [selectedLabId, setSelectedLabId] = useState('');
  const [selectedAssessmentName, setSelectedAssessmentName] = useState('');
  const [selectedLabName, setSelectedLabName] = useState('');
  const [selectedAssignmentName, setSelectedAssignmentName] = useState('');

  const [labData, setLabData] = useState([]);
  const [traineeCountData, setTraineeCountData] = useState([]);
  const [totalTrainees, setTotalTrainees] = useState(0);
  
  // Use external searchTerm if provided, otherwise use internal state
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = setExternalSearchTerm || setInternalSearchTerm;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Internal search term for step 2 (assignment search)
  const [internalAssignmentSearchTerm, setInternalAssignmentSearchTerm] = useState("");
  const assignmentSearchTerm = externalAssignmentSearchTerm !== undefined ? externalAssignmentSearchTerm : internalAssignmentSearchTerm;
  const setAssignmentSearchTerm = setExternalAssignmentSearchTerm || setInternalAssignmentSearchTerm;

  const [quizInfo, setQuizInfo] = useState([]);
  const [quizScore, setQuizScore] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [updatedQuizResponse, setUpdatedQuizResponse] = useState([]);

  // Notify parent when step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Fetch lab data from editquizview endpoint
  const fetchLabData = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${apiEndpoint}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      if (!data.detail) {
        const activeInstances = data.filter(item => 
          ['Running', 'Stopped', 'Creating', 'Terminated'].includes(item.status)
        );
        setLabData(activeInstances);
      }
    } catch (error) {
      console.error('Error fetching lab data:', error);
    }
  }, [apiEndpoint]);

  // Fetch trainee count data
  const fetchTraineeCountData = useCallback(async () => {
    if (!traineeCountEndpoint) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${traineeCountEndpoint}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      if (!data.detail) {
        const activeInstances = data.filter(item => 
          ['Running', 'Stopped', 'Creating', 'Terminated'].includes(item.status)
        );
        setTraineeCountData(activeInstances);
      }
    } catch (error) {
      console.error('Error fetching trainee count data:', error);
    }
  }, [traineeCountEndpoint]);

  useEffect(() => {
    fetchLabData();
    fetchTraineeCountData();
  }, [fetchLabData, fetchTraineeCountData, alignment, updatedQuizResponse]);

  // Get unique lab names for current alignment
  const getUniqueLabs = () => {
    return [...new Set(labData.map(instance => instance[fieldMapping.labNameField]))]
      .map(labName => labData.find(instance => instance[fieldMapping.labNameField] === labName))
      .filter(Boolean);
  };

  const filteredLabs = getUniqueLabs().filter(instance => {
    const labName = instance[fieldMapping.labNameField] || "";
    return labName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedLabs = filteredLabs.map((row, index) => ({
    ...row,
    _rowIndex: index, // Add index for stable ID generation
  })).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Fetch quiz info for selected lab
  const fetchQuizInfo = useCallback(async () => {
    if (!selectedLabId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/quizviewdata/${selectedLabId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      if (!data.detail) {
        setQuizInfo(data);
      }
    } catch (error) {
      console.error('Error fetching quiz info:', error);
    }
  }, [selectedLabId]);

  useEffect(() => {
    if (selectedLabId) {
      fetchQuizInfo();
    }
  }, [selectedLabId, fetchQuizInfo]);

  const filteredQuizInfo = quizInfo.filter(quiz =>
    quiz.assignment_name.toLowerCase().includes(assignmentSearchTerm.toLowerCase())
  );

  const paginatedQuizData = filteredQuizInfo.map((row, index) => ({
    ...row,
    _rowIndex: index, // Add index for stable ID generation
  })).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset state when alignment changes
  useEffect(() => {
    setCurrentStep(1);
    setSelectedLabId('');
    setSelectedAssessmentName('');
    setSelectedLabName('');
    setSelectedAssignmentName('');
    setQuizScore([]);
    if (setExternalSearchTerm) {
      setExternalSearchTerm("");
    } else {
      setInternalSearchTerm("");
    }
    if (setExternalAssignmentSearchTerm) {
      setExternalAssignmentSearchTerm("");
    } else {
      setInternalAssignmentSearchTerm("");
    }
    setPage(0);
    setTotalTrainees(0);
  }, [alignment, setExternalSearchTerm, setExternalAssignmentSearchTerm]);

  const handleAssessmentCancel = () => {
    setCurrentStep(1);
    setSelectedLabId('');
    setSelectedAssessmentName('');
    setSelectedLabName('');
    setSelectedAssignmentName('');
    setQuizScore([]);
  };

  const handleContinue = () => {
    setCurrentStep(prev => prev + 1);
    setSearchTerm("");
    setPage(0);
  };

  const handleLabSelect = (labId, labName) => {
    setSelectedLabId(labId);
    setSelectedLabName(labName);
    
    // Find matching lab to get trainee count
    const matchingLab = traineeCountData.find(lab => {
      // Handle both string and number comparisons, and different ID field names
      const labIdValue = lab[fieldMapping.labIdField] || lab.id || lab.lab_id;
      const compareId = typeof labId === 'string' ? parseInt(labId) : labId;
      return labIdValue === compareId || labIdValue === labId || 
             (typeof labIdValue === 'string' && parseInt(labIdValue) === compareId);
    });
    
    if (matchingLab) {
      setTotalTrainees(parseInt(matchingLab.number_of_trainee) || 0);
    } else {
      // Fallback: try to get from labData if traineeCountData doesn't have it
      const labFromMainData = labData.find(lab => {
        const labIdValue = lab[fieldMapping.labIdField] || lab.id || lab.lab_id;
        const compareId = typeof labId === 'string' ? parseInt(labId) : labId;
        return labIdValue === compareId || labIdValue === labId;
      });
      if (labFromMainData) {
        setTotalTrainees(parseInt(labFromMainData.no_of_trainee) || 0);
      }
    }
  };

  const handleAssessmentSelect = (event) => {
    const assessmentName = event.target.value;
    const selectedQuiz = quizInfo.find(quiz => quiz.name === assessmentName);
    
    if (selectedQuiz) {
      setSelectedAssessmentName(assessmentName);
      setSelectedAssignmentName(selectedQuiz.assignment_name);
    }
  };

  const handleViewScores = async () => {
    if (!selectedLabId || !selectedAssessmentName) return;

    try {
      // URL encode the assessment name to handle special characters
      const encodedAssessmentName = encodeURIComponent(selectedAssessmentName);
      const url = `${process.env.REACT_APP_API_URL}/api/quiztraineescoreview/${selectedLabId}/${encodedAssessmentName}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      });

      // Parse response as JSON
      let data;
      try {
        const responseText = await response.text();
        if (responseText) {
          data = JSON.parse(responseText);
        } else {
          data = {};
        }
      } catch (jsonError) {
        // If response is not JSON, log the error
        console.error('Failed to parse response as JSON:', jsonError);
        console.error('Response status:', response.status, response.statusText);
        return;
      }
      
      if (!response.ok) {
        // Handle error response
        const errorMessage = data.detail || data.message || `HTTP ${response.status}: ${response.statusText}`;
        console.error('Error fetching quiz scores:', errorMessage);
        console.error('URL:', url);
        console.error('Selected Lab ID:', selectedLabId);
        console.error('Selected Assessment Name:', selectedAssessmentName);
        // You might want to show an alert or notification here
        return;
      }
      
      if (data.detail) {
        // API returned an error detail
        console.error('API Error:', data.detail);
        return;
      }
      
      // Success - set the quiz score data
      setQuizScore(data);
      setCurrentStep(3);
    } catch (error) {
      console.error('Error fetching quiz scores:', error);
      console.error('Selected Lab ID:', selectedLabId);
      console.error('Selected Assessment Name:', selectedAssessmentName);
      // You might want to show an alert or notification here
    }
  };

  const handleDownload = () => {
    const formattedData = quizScore.map(instance => ({
      Lab_Name: selectedLabName,
      Assessment_Name: instance.name,
      Assignment_Name: selectedAssignmentName,
      Trainee_Username: instance.trainee_username,
      Trainee_Name: instance.trainee_name,
      Score: instance.grade
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Quiz Scores');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, 'quiz_scores.xlsx');
  };

  const handleDone = () => {
    handleAssessmentCancel();
  };


  // Determine DataGrid columns based on field mapping
  const labIdColumn = {
    field: fieldMapping.labIdField,
    headerName: 'Lab ID',
    flex: 0.7,
    headerAlign: 'center',
    align: 'center',
  };

  const labNameColumn = {
    field: fieldMapping.labNameField,
    headerName: 'Lab Name',
    flex: 1.2,
    headerAlign: 'center',
    align: 'center',
  };

  const startDateColumn = {
    field: fieldMapping.startDateField,
    headerName: 'Start Date',
    flex: 0.9,
    headerAlign: 'center',
    align: 'center',
  };

  const endDateColumn = {
    field: fieldMapping.endDateField,
    headerName: 'End Date',
    flex: 0.9,
    headerAlign: 'center',
    align: 'center',
  };

  // Render Step 1: Lab Selection
  const renderStep1 = () => (
    <Container maxWidth={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0, mt: 2 }}>
        <Box sx={{ 
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1, 
        }}>
          {paginatedLabs.length > 0 ? (
            <Box sx={{ width: '100%' }}>
              <DataGrid
                density="standard"
                autoHeight
                rows={paginatedLabs}
                getRowHeight={() => 60}
                getRowId={(row) => {
                  // Ensure we always return a unique ID, handling different field names
                  const id = row[fieldMapping.labIdField] || row.id || row.lab_id || row.lab_Id;
                  if (id !== undefined && id !== null) {
                    return String(id);
                  }
                  // Fallback: create a unique ID from available fields
                  const name = row[fieldMapping.labNameField] || row.lab_Name || row.lab_name || '';
                  const date = row.date || '';
                  const index = row._rowIndex !== undefined ? row._rowIndex : '';
                  return `${name}_${date}_${index}` || `row_${name}`;
                }}
                columns={[
                  {
                    field: 'radioButton',
                    headerName: 'Select Lab',
                    flex: 1,
                    headerAlign: 'center',
                    align: 'center',
                    renderCell: (params) => (
                      <Radio
                        checked={selectedLabId === params.row[fieldMapping.labIdField]}
                        onChange={() => handleLabSelect(params.row[fieldMapping.labIdField], params.row[fieldMapping.labNameField])}
                        value={params.row[fieldMapping.labIdField]}
                        name="labRadio"
                      />
                    ),
                  },
                  labIdColumn,
                  labNameColumn,
                  startDateColumn,
                  endDateColumn,
                  {
                    field: 'date',
                    headerName: 'Assessment Date',
                    flex: 1,
                    headerAlign: 'center',
                    align: 'center',
                    renderCell: (params) => {
                      if (!params.value) return <span></span>;
                      const parts = params.value.split('/');
                      return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : params.value;
                    },
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    flex: 0.9,
                    headerAlign: 'center',
                    align: 'center',
                    renderCell: (params) => (
                      <span style={{ 
                        color: STATUS_COLORS[params.value] || 'black', 
                        fontWeight: 'bold' 
                      }}>
                        {params.value}
                      </span>
                    ),
                  },
                  {
                    field: 'no_of_trainee',
                    headerName: 'No. of Trainee',
                    flex: 1,
                    headerAlign: 'center',
                    align: 'center',
                  },
                ]}
                sx={{
                    '& .MuiDataGrid-virtualScroller': {
                      overflowX: 'hidden',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      padding: '12px 8px',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'action.hover',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                      },
                    },
                    '& .MuiDataGrid-row': {
                      minHeight: '60px !important',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    },
                  }}
                hideFooter
                disableColumnMenu
                disableRowSelectionOnClick
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 2, pt: 1 }}>
                <TablePagination
                  component="div"
                  count={filteredLabs.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography align="center" sx={{ p: 2 }}>
              No Data Available
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Tooltip title="Cancel" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ mx: 1, borderRadius: '50px', px: 3, py: 1 }}
              onClick={handleAssessmentCancel}
            >
              Cancel
            </Button>
          </Tooltip>

          <Tooltip title="Continue" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ 
                mx: 1, 
                borderRadius: '50px', 
                px: 3, 
                py: 1,
                opacity: selectedLabId ? 1 : 0.5 
              }}
              onClick={selectedLabId ? handleContinue : undefined}
              disabled={!selectedLabId}
            >
              Continue
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Container>
  );

  // Render Step 2: Assessment Selection
  const renderStep2 = () => (
    <Container maxWidth={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0, mt: 2 }}>
        <Box sx={{ 
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}>
        {paginatedQuizData.length > 0 ? (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              density="standard"
              autoHeight
              rows={paginatedQuizData}
              getRowHeight={() => 60}
              getRowId={(row) => {
                // Ensure we always return a unique ID
                const id = row.lab_id || row.id;
                if (id !== undefined && id !== null) {
                  return String(id);
                }
                // Fallback: create a unique ID from available fields
                const name = row.name || '';
                const assignment = row.assignment_name || '';
                const date = row.date || '';
                const index = row._rowIndex !== undefined ? row._rowIndex : '';
                return `${name}_${assignment}_${date}_${index}` || `row_${name}_${assignment}`;
              }}
              sx={{
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'hidden',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  padding: '12px 8px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                },
                '& .MuiDataGrid-row': {
                  minHeight: '60px !important',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              }}
              columns={[
                {
                  field: 'radioButton',
                  headerName: 'Select Assignment',
                  flex: 1,
                  minWidth: 220,
                  headerAlign: 'center',
                  align: 'center',
                  renderCell: (params) => (
                    <Radio
                      value={params.row.name}
                      checked={selectedAssessmentName === params.row.name}
                      onChange={handleAssessmentSelect}
                    />
                  ),
                },
                { field: 'lab_id', headerName: 'Lab ID', flex: 0.8, minWidth: 120, headerAlign: 'center', align: 'center' },
                { field: 'name', headerName: 'Assessment Question Name', flex: 1, minWidth: 240, headerAlign: 'center', align: 'center' },
                { field: 'assignment_name', headerName: 'Assignment Name', flex: 1, minWidth: 220, headerAlign: 'center', align: 'center' },
                { field: 'date', headerName: 'Assessment Date', flex: 0.9, minWidth: 180, headerAlign: 'center', align: 'center' },
              ]}
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 2, pt: 1 }}>
              <TablePagination
                component="div"
                count={filteredQuizInfo.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </Box>
        ) : (
          <Typography align="center" sx={{ p: 2 }}>
            No Data Available
          </Typography>
        )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Tooltip title="Cancel" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ mx: 1, borderRadius: '50px', px: 3, py: 1 }}
              onClick={handleAssessmentCancel}
            >
              Cancel
            </Button>
          </Tooltip>

          <Tooltip title="Continue" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ 
                mx: 1, 
                borderRadius: '50px', 
                px: 3, 
                py: 1,
                opacity: selectedAssessmentName ? 1 : 0.5 
              }}
              onClick={selectedAssessmentName ? handleViewScores : undefined}
              disabled={!selectedAssessmentName}
            >
              Continue
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Container>
  );

  // Render Step 3: Score Details
  const renderStep3 = () => (
    <>
      <Grid container justifyContent="center" sx={{ mt: 2, mb: 2 }}>
        <Grid item xs={12}>
          <Typography
            align="center"
            sx={{ fontSize: "x-large", lineHeight: "2.5" }}
            color="textPrimary"
            variant="body1"
          >
            Score Details
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
        <PieChart
          series={[{
            data: [
              { id: 0, value: totalTrainees, label: 'Total Trainees' },
              { id: 1, value: quizScore.length, label: 'Trainees Completed' }
            ]
          }]}
          width={400}
          height={200}
        />

        {quizScore.length > 0 && (
          <BarChart
            xAxis={[{ 
              scaleType: 'band', 
              data: quizScore.map(score => score.trainee_name) 
            }]}
            series={[{ data: quizScore.map(score => score.grade) }]}
            width={400}
            height={200}
            yAxis={[{
              scaleType: 'linear',
              domain: { 
                min: 0, 
                max: Math.ceil(Math.max(...quizScore.map(score => score.grade)) / 10) * 10 || 10 
              },
              label: 'Grade'
            }]}
          />
        )}
      </Box>

      <Card sx={{ mt: 2 }}>
        {quizScore.length > 0 && (
          <>
            <Box sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              p: 1 
            }}>
              <Typography variant="h6">
                Total Trainees: {totalTrainees} | Completed: {quizScore.length}
              </Typography>
              <Tooltip title="Download scores" arrow>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleDownload}
                  sx={{ borderRadius: '50px', px: 3 }}
                >
                  Download
                </Button>
              </Tooltip>
            </Box>
          </>
        )}

        <Box sx={{ width: '100%', mt: 2, px: { xs: 1, sm: 2 } }}>
          <DataGrid
            rows={quizScore}
            getRowId={(row) => row.trainee_name}
            getRowHeight={() => 60}
            density="standard"
            columns={[
              { field: 'lab_id', headerName: 'Lab Id', flex: 0.5, headerAlign: 'center', align: 'center' },
              { field: 'labName', headerName: 'Lab Name', flex: 1, headerAlign: 'center', align: 'center', valueGetter: () => selectedLabName },
              { field: 'assessmentName', headerName: 'Assessment Name', flex: 1, headerAlign: 'center', align: 'center', valueGetter: () => selectedAssessmentName },
              { field: 'assignmentName', headerName: 'Assignment Name', flex: 1, headerAlign: 'center', align: 'center', valueGetter: () => selectedAssignmentName },
              { field: 'trainee_username', headerName: 'Trainee Username', flex: 1, headerAlign: 'center', align: 'center' },
              { field: 'trainee_name', headerName: 'Trainee Name', flex: 1, headerAlign: 'center', align: 'center' },
              { field: 'grade', headerName: 'Mark Scored', flex: 0.7, headerAlign: 'center', align: 'center' },
            ]}
            hideFooter
            autoHeight
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-cell': {
                padding: '12px 8px',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': {
                minHeight: '60px !important',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 2, pt: 1 }}>
            <TablePagination
              component="div"
              count={quizScore.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Box>
        </Box>

        <Grid container justifyContent="center" sx={{ mt: 2, mb: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Tooltip title="Done" arrow>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleDone}
                  sx={{ borderRadius: '50px', px: 3, py: 1 }}
                >
                  Done
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && selectedLabId && renderStep2()}
      {currentStep === 3 && selectedLabId && selectedAssessmentName && renderStep3()}
    </Box>
  );
};

export default GenericQuizScore;

